import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextField,
  DatePicker,
  NumericInput,
} from '@atgfr8/atg-ui-tailwind';
import { Button, SelectInput } from '@components';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { closeModal } from '@store/modals';
import { updateLoadPropertyAtIndex } from '@store/orders';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { OperationTypeV4 } from '@typeDefinitions/operationTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { getDataValue } from '@utils/formUtils';
import { printLog } from '@utils/utils';
import { lineItemsTypesForCarrierPay } from '@views/order/constants';
import { DateTime } from 'luxon';

const felteFieldError = (v: unknown): string | undefined => {
  if (v == null) return undefined;
  if (Array.isArray(v))
    return v.length ? v.filter(Boolean).join(', ') : undefined;
  return typeof v === 'string' ? v : String(v);
};

import cls from './classes';
import { addEFSFormSchema } from './validation';

type Props = {
  load: ILoadEditViewModel;
  modalId: string;
  tabIndex: number;
};

type EFSFormSchemaFormValues = {
  referenceNum: string;
  description: string;
  checkNumber: string;
  amount: number | null;
  paidDate: string;
  type: string;
  status: string;
  id: number | null;
};

export const AddMissingEfsCheckModal = (props: Props) => {
  const { form, setFields, data, errors } = createForm<EFSFormSchemaFormValues>(
    {
      initialValues: {
        referenceNum: '',
        description: '',
        checkNumber: '',
        amount: null,
        paidDate: DateTime.now().startOf('day').toISO({
          includeOffset: false,
          suppressMilliseconds: true,
        }),
        type: '',
        status: '',
        id: 0,
      },
      extend: validator({ schema: addEFSFormSchema }),
      onSubmit: (values) => {
        const payments = props.load.payments ? [...props.load.payments] : [];

        const updateValues = {
          operationType: 'Insert' as OperationTypeV4,
          referenceNum: values.referenceNum,
          paidDate: values.paidDate,
          checkNumber: '',
          type: 'Payment',
          status: '',
          id: 0,
        };

        payments.push({
          ...updateValues,
          description: `EFS Check For (${values.description || 'undefined'})`,
          checkNumber: values.checkNumber || '',
          amount: values.amount,
          id: -(payments.length + 1),
        });

        payments.push({
          ...updateValues,
          description: `EFS Fee (${values.description || 'undefined'})`,
          amount:
            typeof values.amount === 'number' ? values.amount * 0.04 : null,
          id: -(payments.length + 1),
        });

        updateLoadPropertyAtIndex({ payments: payments });
        closeModal(props.modalId);
      },
      onError: (errors) => {
        printLog(errors, 'Form Errors');
      },
    },
  );

  const commonStyle = { margin: '0 8px 0 0', flex: '1' };

  return (
    <form ref={form}>
      <Grid container gap={1}>
        <div>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell class="text-black!">Ref #</TableHeaderCell>
                <TableHeaderCell class="text-black!">
                  Money Code
                </TableHeaderCell>
                <TableHeaderCell class="text-black!">Amount</TableHeaderCell>
                <TableHeaderCell class="text-black!">Date</TableHeaderCell>
                <TableHeaderCell class="text-black!">Line Item</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow class={cls.tableRow}>
                <TableCell class="align-top!">
                  <TextField
                    id="ref-field"
                    name="referenceNum"
                    label=""
                    error={errors().referenceNum}
                    style={commonStyle}
                    dataTestId={'reference-num-textInput'}
                  />
                </TableCell>
                <TableCell class="align-top!">
                  {
                    <TextField
                      id="money-code-field"
                      name="checkNumber"
                      label=""
                      error={errors().checkNumber}
                      style={commonStyle}
                      dataTestId={'check-number-textInput'}
                    />
                  }
                </TableCell>
                <TableCell class="align-top!">
                  {
                    <NumericInput
                      name="amount"
                      value={data().amount ?? undefined}
                      label=""
                      error={errors().amount ?? []}
                      required
                    />
                  }
                </TableCell>
                <TableCell class="align-top!">
                  <DatePicker
                    value={getDataValue(data, 'paidDate') as string}
                    handleChange={(newDate) => {
                      setFields('paidDate', newDate);
                    }}
                    dataTestId="paid-date-datePicker"
                    error={errors().paidDate ?? []}
                  />
                </TableCell>
                <TableCell class="align-top!">
                  <SelectInput
                    name="type"
                    label=""
                    menuItems={lineItemsTypesForCarrierPay.map((item) => ({
                      label: item.label,
                      value: String(item.value),
                      disabled: item.disabled,
                    }))}
                    value={data().description}
                    onChange={(val) => setFields('description', val)}
                    style={{ ...commonStyle, 'min-width': '200px' }}
                    error={felteFieldError(errors().description) ?? null}
                    dataTestId={formatDataTestId('Line Item', 'selectInput')}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </Grid>
      <Box class="flex justify-end mt-14 gap-3">
        <Button
          label="Save"
          type="submit"
          variant="contained"
          dataTestId={formatDataTestId('Save', 'button')}
        />
        <Button
          label="Cancel"
          variant="text"
          onClick={() => closeModal(props.modalId)}
          dataTestId={formatDataTestId('Cancel', 'button')}
        />
      </Box>
    </form>
  );
};
